import type { CityConfig } from "../city-template/types";

export const boulderConfig: CityConfig = {
  city: "Boulder",
  state: "Colorado",
  stateAbbr: "CO",
  slug: "boulder",
  route: "/cities/boulder",
  title: "Emergency & Fast Passport Renewal Boulder CO | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Boulder, CO — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Boulder County and northern Front Range travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Boulder, CO",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Boulder and northern Front Range travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Boulder%20Colorado%20scenic%20view%20with%20Flatirons%20rock%20formations%20in%20the%20background%2C%20Pearl%20Street%20Mall%20downtown%20area%2C%20University%20of%20Colorado%20campus%20buildings%20visible%2C%20warm%20golden%20hour%20light%2C%20lush%20green%20trees%20with%20mountain%20backdrop%2C%20clean%20and%20modern%20college%20town%20atmosphere%2C%20professional%20landscape%20photography%2C%20warm%20amber%20tones%20with%20cool%20mountain%20blues%2C%20no%20visible%20text%20or%20people%2C%20cinematic%20wide%20angle%20view&width=1800&height=700&seq=boulder-hero-001&orientation=landscape",
  heroImageAlt: "Boulder Colorado with Flatirons rock formations and downtown Pearl Street area",
  geoPosition: "40.0150;-105.2705",
  geoPlacename: "Boulder, Colorado",
  lastModified: "2026-06-02",
  regionalAgency: {
    name: "Denver Passport Agency",
    address: "3151 S. Vaughn Way, Aurora, CO 80014",
    distance: "Approximately 30 miles southeast — about 45 minutes via US-36",
  },
  nearbyCitiesZips: [
    { name: "Downtown Boulder", zips: "80302" },
    { name: "The Hill", zips: "80302" },
    { name: "North Boulder", zips: "80304" },
    { name: "South Boulder", zips: "80305" },
    { name: "Gunbarrel", zips: "80301" },
    { name: "Table Mesa", zips: "80305" },
    { name: "Longmont", zips: "80501–80504" },
    { name: "Louisville", zips: "80027" },
    { name: "Lafayette", zips: "80026" },
    { name: "Nederland", zips: "80466" },
    { name: "Erie", zips: "80516" },
    { name: "Superior", zips: "80027" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Boulder departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Denver", route: "/cities/denver" },
    { name: "Emergency passport help in Colorado Springs", route: "/cities/colorado-springs" },
    { name: "Urgent passport services in Fort Collins", route: "/cities/fort-collins" },
    { name: "Same-day passport help in Longmont", route: "/cities/boulder" },
    { name: "Expedited passport services in Louisville", route: "/cities/boulder" },
    { name: "Passport courier services in Lafayette", route: "/cities/boulder" },
    { name: "Rush passport help in Nederland", route: "/cities/boulder" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Boulder remotely via secure courier. We do not have a walk-in office in Boulder — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Boulder travelers.",
  fpcOfficeDistance: "Serving all of Boulder County and the northern Front Range",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Boulder County and the northern Front Range. From downtown Boulder to the mountain communities, from the university to the tech corridor — we help Boulder travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Boulder neighborhoods and nearby communities including Longmont, Louisville, Lafayette, Nederland, and Erie. If you are in Boulder County or the northern Front Range, we have you covered.",
  howItWorksIntro: "Most Boulder travelers complete everything online and ship documents overnight. No need to drive to Denver or wait in line at the regional agency. Our emergency and expedited passport process is designed for Boulder travelers who need fast, reliable results.",
  mistakesIntro: "With CU Boulder students studying abroad and Boulder tech professionals traveling internationally, Boulder travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Boulder travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Boulder? Here are the most common ones we hear from Boulder County travelers.",
  statePageRoute: "/states/colorado",
  statePageName: "Colorado",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Boulder international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Emily S.",
      location: "CU Boulder Student",
      rating: 5,
      text: "Study abroad program started in 5 days and my passport was expired. Fast Passport Center got me a new one in 36 hours. I made my flight to Spain.",
      service: "Emergency Passport",
    },
    {
      name: "Thomas K.",
      location: "North Boulder, CO",
      rating: 5,
      text: "Needed to renew for a tech conference in Berlin. The process was smooth, they reviewed everything, and I had my passport in 4 days.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa M.",
      location: "Louisville, CO",
      rating: 5,
      text: "Lost my passport before a family trip to Costa Rica. They walked me through the DS-64 process and got me a replacement in 2 days. Saved our vacation.",
      service: "Lost Passport",
    },
    {
      name: "Dr. James R.",
      location: "Table Mesa, Boulder",
      rating: 5,
      text: "As a researcher who travels internationally frequently, I can't afford delays. Fast Passport Center has been my go-to for years. Reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "bld-1",
      name: "Boulder Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1905 15th St",
      city: "Boulder",
      state: "CO",
      zip: "80302",
      phone: "(303) 442-1055",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Pearl Street Mall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "bld-2",
      name: "Table Mesa Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2515 Broadway",
      city: "Boulder",
      state: "CO",
      zip: "80304",
      phone: "(303) 444-0941",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from Table Mesa Shopping Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "bld-3",
      name: "Longmont Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 Coffman St",
      city: "Longmont",
      state: "CO",
      zip: "80501",
      phone: "(303) 772-2570",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Longmont",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Longmont and east Boulder County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "bld-4",
      name: "Louisville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "566 S. Via Firenze",
      city: "Louisville",
      state: "CO",
      zip: "80027",
      phone: "(303) 665-2448",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Louisville downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "bld-5",
      name: "Lafayette Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "409 W. Emma St",
      city: "Lafayette",
      state: "CO",
      zip: "80026",
      phone: "(303) 665-7496",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from Lafayette downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Lafayette and Erie residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "bld-6",
      name: "Boulder County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "1750 33rd St",
      city: "Boulder",
      state: "CO",
      zip: "80301",
      phone: "(303) 441-3500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "2 miles from downtown Boulder",
      website: "https://www.bouldercounty.org/",
      websiteLabel: "Boulder County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Denver (30 mi)", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Boulder photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Boulder acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Boulder applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Denver regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Boulder families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Boulder travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Boulder." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Boulder address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Boulder?",
      a: "Fast Passport Center offers emergency passport processing in Boulder in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Boulder travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Boulder?",
      a: "Same-day passports are not available in Boulder. The closest regional passport agency is in Denver (30 miles southeast). For urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Boulder residents without making the drive to Denver.",
    },
    {
      q: "Where can I get a fast passport renewal in Boulder?",
      a: "Fast Passport Center offers fast passport renewal in Boulder with no office visit required. We serve all neighborhoods including downtown, The Hill, North Boulder, South Boulder, and Gunbarrel. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Boulder?",
      a: "The fastest way to get an urgent passport in Boulder is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "How fast can I get a passport in Boulder?",
      a: "With Fast Passport Center's emergency courier service, Boulder travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Denver Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight from Boulder. Our couriers handle the in-person delivery to State Department facilities. No need to make the 30-mile drive to Denver.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Boulder?",
      a: "Popular options include the Boulder Main Post Office (80302), Table Mesa Post Office (80304), Longmont Post Office (80501), and Boulder County Clerk (80301). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in a nearby town like Longmont or Louisville?",
      a: "We serve all of Boulder County and surrounding communities including Longmont, Louisville, Lafayette, Nederland, and Erie. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Boulder?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Boulder?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Boulder offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Boulder?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Boulder?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Boulder families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Pearl Street", address: "2800 Pearl St, Boulder, CO 80301", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Baseline", address: "4800 Baseline Rd, Boulder, CO 80303", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — 28th Street", address: "2525 28th St, Boulder, CO 80301", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — North Boulder", address: "2525 Broadway, Boulder, CO 80304", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Longmont", address: "745 E. Ken Pratt Blvd, Longmont, CO 80501", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Louisville", address: "1370 E. South Boulder Rd, Louisville, CO 80027", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Lafayette", address: "2750 N. 113th St, Lafayette, CO 80026", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Superior", address: "1300 E. Center Cross Dr, Superior, CO 80027", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Boulder",
      address: "2850 Iris Ave, Boulder, CO 80301",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Longmont",
      address: "915 S. Main St, Longmont, CO 80501",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Louisville",
      address: "350 E. South Boulder Rd, Louisville, CO 80027",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Lafayette",
      address: "137 E. South Boulder Rd, Lafayette, CO 80026",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for Boulder County east. Extended hours available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "DEN",
      name: "Denver International Airport",
      distance: "45 miles from Boulder via US-36",
      address: "8500 Peña Blvd, Denver, CO 80249",
      note: "The primary international airport for Boulder travelers. Direct bus service (BRT) available from Boulder to DEN.",
      flightUrl: "https://www.flydenver.com/",
      flightLabel: "DEN Flight Info",
      featured: true,
    },
    {
      code: "COS",
      name: "Colorado Springs Airport",
      distance: "110 miles from Boulder",
      address: "7770 Milton E. Proby Pkwy, Colorado Springs, CO 80916",
      note: "Regional airport alternative for southern Colorado connections.",
      flightUrl: "https://www.coloradosprings.gov/airport",
      flightLabel: "Colorado Springs Airport",
      featured: false,
    },
  ],
};